<template>
  <div class="match-game">
    <LevelComplete
      v-if="isGameComplete"
      message="Congratulations!"
    />
    <div v-else>
      <h1 class="title">
        Match the Objects to Their Colors!
      </h1>

      <!-- Emojis above -->
      <div class="emoji-grid">
        <div
          v-for="(item, index) in shuffledEmojis"
          :key="'emoji-' + index"
          class="emoji-draggable"
          :style="draggedEmoji === item ? draggedStyle : {}"
          draggable="true"
          @dragstart="handleDragStart($event, item)"
          @click="speakObject(item.itemName)"
          @touchstart="handleTouchStart($event, item)"
          @touchmove="handleTouchMove($event)"
          @touchend="handleTouchEnd($event)"
        >
          {{ item.emoji }}
        </div>
      </div>

      <!-- Colors below -->
      <div class="color-grid">
        <div
          v-for="(item, index) in shuffledColors"
          :key="'color-' + index"
          :ref="`color-${index}`"
          class="color-slot"
          :style="{ backgroundColor: item.colorValue }"
          @dragover.prevent
          @drop="handleDrop($event, item)"
          @click="speakColor(item.colorName)"
        >
          <span v-if="item.matched">{{ item.emoji }}</span>
        </div>
      </div>

      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      items: [
        {
          emoji: "🍓",
          itemName: "Strawberry",
          colorName: "red",
          colorValue: "#FF0000",
          matched: false,
        },
        {
          emoji: "🍌",
          itemName: "Banana",
          colorName: "yellow",
          colorValue: "#FFFF00",
          matched: false,
        },
        {
          emoji: "🍇",
          itemName: "Grapes",
          colorName: "purple",
          colorValue: "#800080",
          matched: false,
        },
        {
          emoji: "🍏",
          itemName: "Apple",
          colorName: "green",
          colorValue: "#008000",
          matched: false,
        },
        {
          emoji: "🍊",
          itemName: "Orange",
          colorName: "orange",
          colorValue: "#FFA500",
          matched: false,
        },
      ],
      shuffledEmojis: [],
      shuffledColors: [],
      draggedEmoji: null,
      draggedStyle: {},
      message: "",
      isGameComplete: false,
    };
  },
  mounted() {
    this.setupGame();
    // Setup speech voices
    this.setupVoices();
  },
  methods: {
    setupGame() {
      // Shuffle the arrays
      this.shuffledEmojis = this.shuffleArray([...this.items]);
      this.shuffledColors = this.shuffleArray([...this.items]);
    },
    shuffleArray(array) {
      return array.sort(() => Math.random() - 0.5);
    },
    handleDragStart(event, item) {
      event.dataTransfer.setData("text/plain", item.emoji);
      this.draggedEmoji = item;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    handleDrop(event, targetItem) {
      const draggedEmoji = event.dataTransfer.getData("text/plain");
      if (this.draggedEmoji && this.draggedEmoji.emoji === draggedEmoji) {
        if (this.draggedEmoji.colorName === targetItem.colorName) {
          if (!targetItem.matched) {
            targetItem.matched = true;
            targetItem.emoji = this.draggedEmoji.emoji;
            this.message = "✅ Correct!";
            this.speakOutLoud(targetItem.colorName);
            this.playSound(
              "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
            );
            this.draggedEmoji = null;
            // Check if game is complete
            if (this.shuffledColors.every((item) => item.matched)) {
              this.isGameComplete = true;
              this.speakOutLoud("Congratulations!");
              setTimeout(() => {
                this.$router.push("/colors");
              }, 4000);
            }
          } else {
            this.message = "❌ Already matched!";
          }
        } else {
          this.message = "❌ Try Again!";
        }
      }
    },
    resetGame() {
      this.items.forEach((item) => (item.matched = false));
      this.setupGame();
      this.isGameComplete = false;
      this.message = "";
    },
    speakObject(itemName) {
      this.speakOutLoud(itemName);
    },
    speakColor(colorName) {
      this.speakOutLoud(colorName);
    },
    handleTouchStart(event, item) {
      this.draggedEmoji = item;
      this.draggedStyle = {
        position: "absolute",
        left: `${event.touches[0].clientX - 40}px`,
        top: `${event.touches[0].clientY - 40}px`,
        zIndex: 1000,
      };
    },
    handleTouchMove(event) {
      event.preventDefault();
      this.draggedStyle.left = `${event.touches[0].clientX - 40}px`;
      this.draggedStyle.top = `${event.touches[0].clientY - 40}px`;
    },
    handleTouchEnd(event) {
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      // Check each color slot
      for (let i = 0; i < this.shuffledColors.length; i++) {
        const colorEl = this.$refs[`color-${i}`][0];
        const rect = colorEl.getBoundingClientRect();
        if (
          touchEndX >= rect.left &&
          touchEndX <= rect.right &&
          touchEndY >= rect.top &&
          touchEndY <= rect.bottom
        ) {
          this.handleDrop(
            { dataTransfer: { getData: () => this.draggedEmoji.emoji } },
            this.shuffledColors[i]
          );
          break;
        }
      }
      this.draggedEmoji = null;
      this.draggedStyle = {};
    },
  },
};
</script>

<style scoped>
.match-game {
  text-align: center;
}
.title {
  font-size: 2rem;
  margin-bottom: 20px;
}
.emoji-grid {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}
.emoji-draggable {
  cursor: grab;
  border-radius: 10px;
  width: 80px;
  height: 80px;
  font-size: 2.5rem;
  background-color: #81d4fa;
  font-weight: bold;
  margin: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: transform 0.3s;
  box-shadow: -2px 2px 2px 2px lightgray;
}
.emoji-draggable:hover {
  transform: scale(1.1);
}
.color-grid {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 50px;
}
.color-slot {
  width: 80px;
  height: 80px;
  margin: 10px;
  border: 2px solid gray;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 2.5rem;
  color: white;
  box-shadow: 2px 2px 2px 2px lightgray;
  cursor: pointer;
}
.message {
  font-size: 2rem;
  font-weight: bold;
  margin-top: 20px;
}
</style>
